import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { OnlineQrisPanel } from './PaymentDialog/OnlineQrisPanel';
import { qrisViewOf } from '@/lib/onlinePayment';
import { useResumePayment } from '@/hooks/mutations';
import type { ResumePaymentResult, SaleStatus } from '@/types/sale';

interface ResumePaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  /** Amount owed, from the order the cashier selected. */
  total: number;
  /** Fired once the payment concludes as paid, so the list can refresh. */
  onPaid: (invoiceId: string) => void;
}

/**
 * Finish taking payment on an order that was left unpaid.
 *
 * Reuses `MidtransQrisPanel` rather than reimplementing the wait: given an
 * invoice and a QR payload it already polls, listens on the push channel,
 * counts the charge window down and tells an expired code apart from a
 * decline. All this adds is where the data comes from — a stored charge
 * instead of a fresh one.
 *
 * The most valuable outcome is the one with no QR in it at all. The server
 * asks the gateway before answering, so an order the customer already paid —
 * whose webhook never reached us — comes back SUCCESS and lands on the
 * "already paid" branch below without the cashier doing anything else.
 */
export function ResumePaymentDialog({
  open,
  onOpenChange,
  invoiceId,
  total,
  onPaid,
}: ResumePaymentDialogProps) {
  const { mutate: resume, isPending } = useResumePayment();
  const [data, setData] = useState<ResumePaymentResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<SaleStatus>('PENDING');

  useEffect(() => {
    if (!open || !invoiceId) return;
    setData(null);
    setError(null);
    setStatus('PENDING');
    resume(invoiceId, {
      onSuccess: (res) => {
        setData(res);
        setStatus(res.status);
      },
      // The server's message names the tender it cannot continue, which is
      // the part the cashier acts on — so it is shown rather than replaced.
      onError: (e: unknown) => {
        const msg =
          (e as { message?: string })?.message ??
          'Pembayaran ini tidak bisa dilanjutkan dari daftar pesanan.';
        setError(msg);
      },
    });
  }, [open, invoiceId, resume]);

  const settled = status === 'SUCCESS' || status === 'SETTLEMENT';
  // Neutral block first, Midtrans's fields as the fallback (lib/onlinePayment).
  const view = data ? qrisViewOf(data) : null;

  useEffect(() => {
    if (settled) {
      const t = setTimeout(() => {
        onPaid(invoiceId);
        onOpenChange(false);
      }, 1200);
      return () => clearTimeout(t);
    }
  }, [settled, invoiceId, onPaid, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Lanjutkan pembayaran</DialogTitle>
          <DialogDescription>
            {invoiceId} — selesaikan pembayaran untuk mencetak tiket.
          </DialogDescription>
        </DialogHeader>

        {isPending && !data && !error ? (
          <div className="flex flex-col items-center gap-3 py-10 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm">Memeriksa status pembayaran…</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <p className="max-w-sm text-sm text-muted-foreground">{error}</p>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Tutup
            </Button>
          </div>
        ) : settled ? (
          // Reached without the cashier doing anything when the sync that runs
          // server-side found the payment already settled.
          <div className="flex flex-col items-center gap-2 py-10 text-center">
            <CheckCircle2 className="h-14 w-14 text-success" />
            <p className="font-display text-lg font-semibold text-success">
              Pembayaran sudah diterima
            </p>
            <p className="text-sm text-muted-foreground">Tiket siap dicetak.</p>
          </div>
        ) : data ? (
          <OnlineQrisPanel
            total={total}
            invoiceId={invoiceId}
            // Never the Snap `token` as a QR (see the note in PaymentDialog);
            // a Snap charge re-opens Midtrans's own page instead.
            qrString={view?.qrString ?? null}
            qrImageUrl={view?.qrImageUrl ?? null}
            snapToken={view?.snapToken ?? null}
            gateway={view?.gateway ?? null}
            socketConfig={data.socket ?? null}
            paymentExpire={view?.paymentExpire ?? null}
            status={status}
            onStatus={setStatus}
            // The ending is already known when a concluded payment is
            // re-opened; without it an expired code renders as a decline.
            initialGatewayStatus={data.paymentTransactionStatus ?? null}
            // The server's own answer; it was on the response and never passed.
            initialRetrySafe={data.retrySafe}
            cancelLabel="Tutup"
            onSuccess={() => {
              onPaid(invoiceId);
              onOpenChange(false);
            }}
            // No `onRestart`: a new charge needs the cart, and this dialog only
            // has an invoice. An expired code is re-taken from the sale screen.
            onCancel={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
